import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { CoinAvatar } from '../components/CoinAvatar';
import { CtaSection } from '../components/Sections';
import { FEATURED_COINS } from '../lib/content';
import { buildTitle } from '../lib/site';
import { useI18n } from '../i18n';

interface Currency {
  ticker: string;
  name: string;
  network?: string;
  image?: string;
}

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

const baseTicker = (t: string) => t.split('_')[0].toUpperCase();
const networkOf = (t: string) => {
  const parts = t.split('_');
  return parts[1] ? parts[1].toUpperCase() : null;
};

const NETWORK_COLORS: Record<string, string> = {
  ETH: '#627eea', TRX: '#e0003c', TRON: '#e0003c', ARB: '#f5851b', ARBITRUM: '#f5851b', SOL: '#14f195',
  BSC: '#f0b90b', OP: '#f5851b', OPTIMISM: '#f5851b', AVAX: '#e84142', MATIC: '#8247e3', POLYGON: '#8247e3',
};

export function CoinsPage() {
  const { locale: t, fmt } = useI18n();
  const p = t.pages.coins;
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [query, setQuery] = useState('');
  const [network, setNetwork] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/currencies`)
      .then((r) => r.json())
      .then((data) => {
        if (!mounted || !data.success || !Array.isArray(data.data)) return;
        const seen = new Set<string>();
        const list: Currency[] = [];
        for (const c of data.data) {
          const t = c.network && c.network !== c.ticker ? `${c.ticker}_${c.network}` : c.ticker;
          if (seen.has(t)) continue;
          seen.add(t);
          list.push({ ticker: t, name: c.name || t, network: c.network || '', image: c.image || undefined });
        }
        list.sort((a, b) => a.ticker.localeCompare(b.ticker));
        setCurrencies(list);
      })
      .catch(() => {
        setCurrencies(FEATURED_COINS.map((c) => ({ ticker: c.ticker, name: c.name })));
      });
    return () => { mounted = false; };
  }, []);

  // Build the list of available networks from the data.
  const networks = useMemo(() => {
    const counts = new Map<string, number>();
    currencies.forEach((c) => {
      const net = networkOf(c.ticker);
      if (net) counts.set(net, (counts.get(net) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([net, count]) => ({ net, count }));
  }, [currencies]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return currencies.filter((c) => {
      if (network && networkOf(c.ticker) !== network) return false;
      if (!q) return true;
      return (
        c.ticker.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.network && c.network.toLowerCase().includes(q))
      );
    });
  }, [currencies, query, network]);

  return (
    <>
      <Seo
        title={buildTitle(p.title)}
        description={p.lead}
        path="/coins"
      />

      <section className="page-header">
        <div className="container">
          <span className="eyebrow">{p.eyebrow}</span>
          <h1>{p.title}</h1>
          <p>{p.lead}</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Featured */}
          <div className="section-head">
            <span className="eyebrow">{p.spotlightEyebrow}</span>
            <h2>{p.spotlightTitle}</h2>
          </div>
          <div className="grid grid-3" style={{ marginBottom: 64 }}>
            {FEATURED_COINS.map((c) => (
              <div className="card card-feature" key={c.ticker}>
                <div className="card-icon"><CoinAvatar ticker={c.ticker} size={26} ring /></div>
                <h3>{c.name} <span className="mono" style={{ color: 'var(--gold)', fontSize: 14 }}>{c.symbol}</span></h3>
                <p>{t.featuredBlurbs[c.ticker as keyof typeof t.featuredBlurbs] ?? c.blurb}</p>
              </div>
            ))}
          </div>

          {/* Full list */}
          <div className="section-head">
            <span className="eyebrow">{p.catalogueEyebrow}</span>
            <h2>{p.catalogueTitle} <span style={{ color: 'var(--gold)' }}>({filtered.length})</span></h2>
          </div>

          {/* Network filter chips */}
          {networks.length > 0 && (
            <div className="filter-chips">
              <button
                className={`chip${network === null ? ' active' : ''}`}
                onClick={() => setNetwork(null)}
              >
                {p.allChips}
              </button>
              {networks.slice(0, 10).map(({ net, count }) => (
                <button
                  key={net}
                  className={`chip${network === net ? ' active' : ''}`}
                  onClick={() => setNetwork(network === net ? null : net)}
                >
                  <span
                    className="chip-dot"
                    style={{ background: NETWORK_COLORS[net] || '#888' }}
                  />
                  {net}
                  <span style={{ opacity: 0.6 }}>{count}</span>
                </button>
              ))}
            </div>
          )}

          <div className="currency-search-wrapper" style={{ maxWidth: 400, marginBottom: 24 }}>
            <input
              className="currency-search-input form-input"
              style={{ height: 44 }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={p.searchPlaceholder}
            />
          </div>

          <div className="coins-grid">
            {filtered.slice(0, 300).map((c) => {
              const net = networkOf(c.ticker);
              return (
                <Link
                  to={`/exchange/${c.ticker}-to-btc`}
                  className="coin-chip"
                  key={c.ticker}
                >
                  <CoinAvatar ticker={c.ticker} size={32} imageUrl={c.image} />
                  <div style={{ minWidth: 0 }}>
                    <div className="ticker">
                      {baseTicker(c.ticker)}
                      {net && <span className="coin-network-tag"> · {net}</span>}
                    </div>
                    <div className="name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  </div>
                </Link>
              );
            })}
          </div>
          {filtered.length > 300 && (
            <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
              {fmt(p.showingFirst, { total: filtered.length })}
            </p>
          )}
          {filtered.length === 0 && (
            <p style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>{fmt(p.noMatch, { query })}</p>
          )}
        </div>
      </section>

      <CtaSection />
    </>
  );
}
